import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EventSearchSelect } from '@/components/common/EventSearchSelect'
import { toast } from 'sonner'
import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import { useCalendarEventsQuery } from '@/features/calendar/api/useCalendarEventsQuery'
import type { ICalendarEvent } from '@/features/calendar/interfaces'

import { parseCpdCsv } from '@/features/calendar/utils/parseCpdCsv'
import type { UploadCpdRow } from '@/features/calendar/api/useUploadCpdPoints'
import { useBulkUploadAwards, useGetEventParticipants, useIssueCertificate, useMarkAttendance } from '@/utils/api/membership-service'
import type { CPDPointsAwardCreateDTO } from '@/utils/api/membership-service/models/cpdpoints-award-create-dt-o'
import type { RestPageCPDParticipant } from '@/utils/api/membership-service/models/rest-page-cpd-participant'
import type { CPDEventParticipantResponseDTO } from '@/utils/api/membership-service/models/cpdevent-participant-response-dt-o'
import { FileSpreadsheet, UploadCloud, X } from 'lucide-react'
import { cn } from '@/lib/utils'

function CPDRegistrations() {
  const eventsQuery = useCalendarEventsQuery()
  const events = (eventsQuery.data || []) as ICalendarEvent[]

  // default select the first event when list becomes available so user always sees data
  const [selectedEventId, setSelectedEventId] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (!selectedEventId && events.length > 0) {
      setSelectedEventId(String(events[0].id))
    }
  }, [events, selectedEventId])
  const selectedEvent = useMemo(() => events.find((e: ICalendarEvent) => String(e.id) === String(selectedEventId)), [events, selectedEventId])

  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [selectedParticipants, setSelectedParticipants] = useState<CPDEventParticipantResponseDTO[]>([])
  const [selectionResetKey, setSelectionResetKey] = useState(0)
  const [isMarkingAttendance, setIsMarkingAttendance] = useState(false)
  const [isIssuingCertificates, setIsIssuingCertificates] = useState(false)
  useEffect(() => {
    setPageIndex(0)
    setSelectedParticipants([])
    setSelectionResetKey((prev) => prev + 1)
  }, [selectedEventId])

  const [stagedUploadRows, setStagedUploadRows] = useState<UploadCpdRow[]>([])
  const [previewHeaders, setPreviewHeaders] = useState<string[]>([])
  const [previewRows, setPreviewRows] = useState<Record<string, string>[]>([])
  const bulkMutation = useBulkUploadAwards()
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // participants for selected event
  const eventIdNumber = selectedEventId ? Number(selectedEventId) : 0
  const participantsParams = useMemo(() => ({ page: pageIndex, size: pageSize }), [pageIndex, pageSize])

  const participantsQuery = useGetEventParticipants<RestPageCPDParticipant>(
    eventIdNumber,
    participantsParams,
    {
      query: {
        enabled: !!selectedEventId,
        placeholderData: (previousData) => previousData,
        select: (response) => (response?.data ?? response) as RestPageCPDParticipant,
      },
    },
  )
  const participantsPage = participantsQuery.data
  const participants: CPDEventParticipantResponseDTO[] = useMemo(() => {
    if (!participantsPage) return []

    const maybeArray = participantsPage as unknown as CPDEventParticipantResponseDTO[] | undefined
    if (Array.isArray(maybeArray)) return maybeArray

    const fallback = participantsPage as unknown as {
      content?: CPDEventParticipantResponseDTO[]
      items?: CPDEventParticipantResponseDTO[]
      participants?: CPDEventParticipantResponseDTO[]
    }

    if (Array.isArray(fallback.content)) return fallback.content
    if (Array.isArray(fallback.items)) return fallback.items
    if (Array.isArray(fallback.participants)) return fallback.participants

    return []
  }, [participantsPage])
  const totalParticipants = participantsPage?.totalElements ?? participants.length

  const markAttendanceMutation = useMarkAttendance()
  const issueCertificateMutation = useIssueCertificate()

  const handlePageChange = useCallback((nextPageIndex: number, nextPageSize: number) => {
    setPageIndex(nextPageIndex)
    setPageSize(nextPageSize)
    setSelectedParticipants([])
    setSelectionResetKey((prev) => prev + 1)
  }, [])

  const handlePageSizeChange = useCallback((nextPageSize: number) => {
    setPageIndex(0)
    setPageSize(nextPageSize)
    setSelectedParticipants([])
    setSelectionResetKey((prev) => prev + 1)
  }, [])

  const markAttendanceForParticipants = useCallback(async (targets: CPDEventParticipantResponseDTO[]) => {
    const validTargets = targets.filter((participant): participant is CPDEventParticipantResponseDTO & { id: number } => typeof participant?.id === 'number')
    if (!validTargets.length) {
      toast.info('Select at least one participant with a valid ID.')
      return
    }

    setIsMarkingAttendance(true)
    try {
      const results = await Promise.allSettled(
        validTargets.map((participant) =>
          markAttendanceMutation.mutateAsync({
            participantId: participant.id,
            data: { attended: true },
          }),
        ),
      )

      const successCount = results.filter((result) => result.status === 'fulfilled').length
      const failureCount = results.length - successCount

      if (successCount > 0) {
        toast.success(`Marked attendance for ${successCount} participant${successCount === 1 ? '' : 's'}.`)
        await participantsQuery.refetch()
      }

      if (failureCount > 0) {
        toast.error(`Failed to mark attendance for ${failureCount} participant${failureCount === 1 ? '' : 's'}.`)
      }
    } catch (error) {
      toast.error('Failed to mark attendance. Please try again.')
    } finally {
      setIsMarkingAttendance(false)
      setSelectedParticipants([])
      setSelectionResetKey((prev) => prev + 1)
    }
  }, [markAttendanceMutation, participantsQuery])

  const issueCertificatesForParticipants = useCallback(async (targets: CPDEventParticipantResponseDTO[]) => {
    const validTargets = targets.filter((participant): participant is CPDEventParticipantResponseDTO & { id: number } => typeof participant?.id === 'number')
    if (!validTargets.length) {
      toast.info('Select at least one participant with a valid ID.')
      return
    }

    setIsIssuingCertificates(true)
    try {
      const results = await Promise.allSettled(
        validTargets.map((participant) =>
          issueCertificateMutation.mutateAsync({
            participantId: participant.id,
          }),
        ),
      )

      const successCount = results.filter((result) => result.status === 'fulfilled').length
      const failureCount = results.length - successCount

      if (successCount > 0) {
        toast.success(`Issued certificates to ${successCount} participant${successCount === 1 ? '' : 's'}.`)
        await participantsQuery.refetch()
      }

      if (failureCount > 0) {
        toast.error(`Failed to issue certificates to ${failureCount} participant${failureCount === 1 ? '' : 's'}.`)
      }
    } catch (error) {
      toast.error('Failed to issue certificates. Please try again.')
    } finally {
      setIsIssuingCertificates(false)
      setSelectedParticipants([])
      setSelectionResetKey((prev) => prev + 1)
    }
  }, [issueCertificateMutation, participantsQuery])

  const markAttendanceForParticipant = useCallback((participant: CPDEventParticipantResponseDTO) => {
    void markAttendanceForParticipants([participant])
  }, [markAttendanceForParticipants])

  const issueCertificateForParticipant = useCallback((participant: CPDEventParticipantResponseDTO) => {
    void issueCertificatesForParticipants([participant])
  }, [issueCertificatesForParticipants])

  const bulkActionButtons = useMemo(() => {
    const noSelection = selectedParticipants.length === 0
    const selectionHasIssued = selectedParticipants.some((p) => p.certificateIssued === true)
    const issueDisabled = noSelection || isIssuingCertificates || selectionHasIssued
    const issueDisabledReason = isIssuingCertificates
      ? 'Issuing certificates… please wait.'
      : noSelection
        ? 'Select at least one participant to issue certificates.'
        : selectionHasIssued
          ? 'One or more selected participants already have certificates.'
          : ''

    return (
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => void markAttendanceForParticipants(selectedParticipants)}
          disabled={selectedParticipants.length === 0 || isMarkingAttendance}
        >
          {isMarkingAttendance ? 'Marking…' : selectedParticipants.length > 1 ? 'Mark attendance (bulk)' : 'Mark attendance'}
        </Button>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                size="sm"
                onClick={() => void issueCertificatesForParticipants(selectedParticipants)}
                disabled={issueDisabled}
                className={cn(
                  issueDisabled ? 'cursor-not-allowed' : '',
                  'disabled:pointer-events-none',
                )}
              >
                {isIssuingCertificates ? 'Issuing…' : selectedParticipants.length > 1 ? 'Issue certificates' : 'Issue certificate'}
              </Button>
            </span>
          </TooltipTrigger>
          {issueDisabledReason && (
            <TooltipContent side="top">
              {issueDisabledReason}
            </TooltipContent>
          )}
        </Tooltip>
      </div>
    )
  }, [isIssuingCertificates, isMarkingAttendance, issueCertificatesForParticipants, markAttendanceForParticipants, selectedParticipants])

  const participantColumns = useMemo<ColumnConfig<CPDEventParticipantResponseDTO>[]>(() => {
    const columns: ColumnConfig<CPDEventParticipantResponseDTO>[] = [
      { key: 'memberName', header: 'Member name' },
      { key: 'memberEmail', header: 'Email' },
      { key: 'memberPhone', header: 'Phone' },
      { key: 'status', header: 'Status' },
      { key: 'attended', header: 'Attended', type: 'boolean' },
      { key: 'pointsEarned', header: 'Points earned', type: 'number' },
      // { key: 'eventPoints', header: 'Event points', type: 'number' },
      {
        key: 'registeredAt',
        header: 'Registered at',
        format: (value: unknown) => (value ? new Date(value as string).toLocaleString() : ''),
      },
      {
        key: 'actions',
        header: 'Actions',
        accessor: () => null,
        cell: (_, participant) => (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={(event) => {
                event.stopPropagation()
                markAttendanceForParticipant(participant)
              }}
              disabled={isMarkingAttendance || typeof participant?.id !== 'number'}
            >
              {isMarkingAttendance ? 'Marking…' : 'Mark attendance'}
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation()
                      issueCertificateForParticipant(participant)
                    }}
                    disabled={
                      isIssuingCertificates ||
                      participant.certificateIssued === true ||
                      typeof participant?.id !== 'number'
                    }
                    className={cn(
                      (isIssuingCertificates || participant.certificateIssued === true || typeof participant?.id !== 'number')
                        ? 'cursor-not-allowed'
                        : '',
                      'disabled:pointer-events-none',
                    )}
                  >
                    {isIssuingCertificates ? 'Issuing…' : 'Issue certificate'}
                  </Button>
                </span>
              </TooltipTrigger>
              {(isIssuingCertificates || participant.certificateIssued === true || typeof participant?.id !== 'number') && (
                <TooltipContent side="top">
                  {isIssuingCertificates
                    ? 'Issuing certificates… please wait.'
                    : participant.certificateIssued === true
                      ? 'Certificate already issued for this participant.'
                      : 'Cannot issue a certificate because the participant is missing an identifier.'}
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        ),
      },
    ]

    return columns
  }, [issueCertificateForParticipant, isIssuingCertificates, isMarkingAttendance, markAttendanceForParticipant])

  const isInitialParticipantsLoad = participantsQuery.isLoading && !participantsQuery.isFetched
  const isTableLoading = participantsQuery.isFetching && participantsQuery.isFetched

  const resetSelection = useCallback(() => {
    setSelectedFileName(null)
    setStagedUploadRows([])
    setPreviewHeaders([])
    setPreviewRows([])
    setIsDragging(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  const processFile = useCallback(async (file: File | null) => {
    if (!file) return

    try {
      const parsed = await parseCpdCsv(file, selectedEvent ? { id: selectedEvent.id, title: selectedEvent.title } : undefined)
      if (!parsed.uploadRows.length) {
        resetSelection()
        toast.error('No valid rows found in the selected file.')
        return
      }

      setSelectedFileName(file.name)
      setStagedUploadRows(parsed.uploadRows)
      setPreviewHeaders(parsed.headers)
      setPreviewRows(parsed.previewRows)
      toast.success(`${parsed.previewRows.length} row${parsed.previewRows.length === 1 ? '' : 's'} parsed`)
    } catch (err) {
      toast.error((err as Error).message)
      resetSelection()
    }
  }, [selectedEvent, resetSelection])

  const handleDownloadTemplate = () => {
    if (!selectedEvent) {
      toast.error('Please select an event to generate template for')
      return
    }

    const headers = [
      'memberId',
      'email',
      'phone',
      'points',
      'eventId',
      'eventName',
      'participantId',
      'participantName',
      'registrationStatus',
      'registeredAt',
    ]

    const toCsvValue = (value: unknown) => {
      const stringValue = value ?? ''
      const text = typeof stringValue === 'string' ? stringValue : String(stringValue)
      if (text.includes('"') || text.includes(',') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`
      }
      return text
    }

    const participantRows =
      participants.length > 0
        ? participants.map((participant) => [
            participant.userId ?? '',
            participant.memberEmail ?? '',
            participant.memberPhone ?? '',
            '',
            String(selectedEvent.id),
            selectedEvent.title ?? '',
            participant.id ?? '',
            participant.memberName ?? '',
            participant.status ?? '',
            participant.registeredAt ?? '',
          ])
        : [['12345', 'jane@example.org', '+256700000000', '', String(selectedEvent.id), selectedEvent.title ?? '', '', 'John Doe', 'registered', '']]

    const csvLines = [
      headers.join(','),
      ...participantRows.map((row) => row.map(toCsvValue).join(',')),
    ]

    const csv = csvLines.join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `cpd-template-${selectedEvent.id}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }



  

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">CPD Registrations</h1>
          <p className="text-muted-foreground mt-2 text-sm">Select an event to view participants. You can still download a template or upload registrations.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Get template dialog */}
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline">Get template</Button>
            </DialogTrigger>
            <DialogContent className="w-full sm:w-[90vw] lg:w-[min(50vw,1040px)]">
              <DialogHeader>
                <DialogTitle>Download CPD template</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="min-w-[320px]">
                  <EventSearchSelect
                    value={selectedEventId}
                    events={events}
                    onValueChange={setSelectedEventId}
                    placeholder="Choose event"
                  />
                </div>

                <div className="flex justify-end">
                  <Button variant="outline" onClick={handleDownloadTemplate}>Download template</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Upload dialog using FilePond */}
          <Dialog>
            <DialogTrigger asChild>
              <Button>Upload template</Button>
            </DialogTrigger>
            <DialogContent className="w-full max-h-[85vh] overflow-y-auto sm:w-[60vw] sm:max-w-[60vw]">
              <DialogHeader>
                <DialogTitle>Upload CPD template</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <p className="text-sm text-muted-foreground">Drop or browse for a CSV file. Nothing is sent to the server until you confirm the import.</p>
                <div className="space-y-4">
                  <Input
                    ref={fileInputRef}
                    id="cpd-upload-input"
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0] ?? null
                      void processFile(file)
                      event.target.value = ''
                    }}
                  />

                  {!selectedFileName && (
                    <div
                      className={cn(
                        'group relative flex cursor-pointer flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed px-10 py-12 text-center transition-colors duration-200',
                        isDragging ? 'border-primary bg-primary/10' : 'border-border/60 bg-muted/30 hover:border-primary hover:bg-muted/40',
                      )}
                      role="button"
                      tabIndex={0}
                      onClick={() => fileInputRef.current?.click()}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          fileInputRef.current?.click()
                        }
                      }}
                      onDragEnter={(event) => {
                        event.preventDefault()
                        setIsDragging(true)
                      }}
                      onDragOver={(event) => {
                        event.preventDefault()
                        event.dataTransfer.dropEffect = 'copy'
                      }}
                      onDragLeave={(event) => {
                        event.preventDefault()
                        const nextTarget = event.relatedTarget as Node | null
                        if (nextTarget && event.currentTarget.contains(nextTarget)) return
                        setIsDragging(false)
                      }}
                      onDrop={(event) => {
                        event.preventDefault()
                        setIsDragging(false)
                        const droppedFile = event.dataTransfer.files?.[0] ?? null
                        void processFile(droppedFile)
                      }}
                    >
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <UploadCloud className="h-6 w-6" aria-hidden="true" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-foreground">Drag &amp; drop your CSV</p>
                        <p className="text-xs text-muted-foreground">Supported format: .csv</p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={(event) => {
                          event.stopPropagation()
                          fileInputRef.current?.click()
                        }}
                      >
                        Browse files
                      </Button>
                    </div>
                  )}

                  {selectedFileName && (
                    <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border/60 bg-background/80 px-4 py-3 text-sm shadow-sm">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
                          <FileSpreadsheet className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                        </span>
                        <div className="space-y-1">
                          <p className="font-medium text-foreground">{selectedFileName}</p>
                          <p className="text-xs text-muted-foreground">
                            {previewRows.length > 0 ? `${previewRows.length} row${previewRows.length === 1 ? '' : 's'} ready to preview` : 'Parsing file…'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Replace file
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={resetSelection}
                          aria-label="Remove selected file"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {previewRows.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex flex-col gap-1 text-sm">
                        <span className="font-medium text-foreground">Preview parsed rows</span>
                        <span className="text-xs text-muted-foreground">Showing first 10 rows of {previewRows.length}</span>
                      </div>
                      <div className="overflow-hidden rounded-xl border border-border/40 shadow-xs">
                        <div className="overflow-x-auto">
                          <table className="min-w-full table-fixed text-sm">
                            <thead className="bg-muted/60">
                              <tr>
                                {previewHeaders.map(header => (
                                  <th key={header} className="px-4 py-2 text-left font-medium text-muted-foreground">
                                    {header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {previewRows.slice(0, 10).map((row, rowIndex) => (
                                <tr key={rowIndex} className="border-t border-border/40">
                                  {previewHeaders.map(header => (
                                    <td key={`${rowIndex}-${header}`} className="px-4 py-2 text-foreground">
                                      {row[header] ?? ''}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={resetSelection}>
                          Clear
                        </Button>
                        <Button
                          onClick={() => {
                            // Map staged rows to server DTO shape
                            const awards: CPDPointsAwardCreateDTO[] = stagedUploadRows.map((r) => ({
                              memberId: r.memberId,
                              eventId: Number(r.eventId),
                              points: r.points,
                              email: r.email || undefined,
                              phone: r.phone || undefined,
                              eventName: r.eventName || undefined,
                              awardedOn: r.awardedOn || undefined,
                            }))

                            bulkMutation.mutate({ data: { awards } }, {
                              onSuccess: async () => {
                                // On success, clear staged selection and refresh participants table data.
                                resetSelection()
                                setSelectedParticipants([])
                                setSelectionResetKey((prev) => prev + 1)
                                await participantsQuery.refetch()
                                toast.success(`${stagedUploadRows.length} row${stagedUploadRows.length === 1 ? '' : 's'} imported`)
                              },
                              onError: (err: any) => toast.error(err?.message ?? 'Import failed'),
                            })
                          }}
                          disabled={bulkMutation.isPending || stagedUploadRows.length === 0}
                        >
                          {bulkMutation.isPending ? 'Importing…' : 'Confirm import'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Prepare upload</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-4">
          <div className="min-w-[320px]">
            <EventSearchSelect
              value={selectedEventId}
              events={events}
              onValueChange={setSelectedEventId}
              placeholder="Choose event"
            />
          </div>

          <div className="flex gap-2">
            {/* Removed inline upload - upload is done in the dialog */}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Event participants</CardTitle>
        </CardHeader>
        <CardContent>
          {participantsQuery.isError ? (
            <p className="text-sm text-destructive">Unable to load participants. Please try again.</p>
          ) : isInitialParticipantsLoad ? (
            <p className="text-sm text-muted-foreground">Loading participants…</p>
          ) : participants.length > 0 ? (
            <EnhancedDataTable
              key={selectedEventId ?? 'participants-table'}
              data={participants}
              columns={participantColumns}
              enableExport
              enableGlobalFilter
              enableRowSelection
              pageSize={pageSize}
              loading={isTableLoading}
              customButtons={bulkActionButtons}
              onSelectedRowsChange={setSelectedParticipants}
              clearSelectionTrigger={selectionResetKey}
              paginationCallbacks={{
                manualPagination: true,
                totalCount: totalParticipants,
                onPageChange: handlePageChange,
                onPageSizeChange: handlePageSizeChange,
              }}
            />
          ) : (
            <p className="text-sm text-muted-foreground">No participants found for the selected event.</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default CPDRegistrations
